import type { ReactElement, ReactNode } from "react";

import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type TooltipConfigs,
} from "../primitives/tooltip";

type TenkoTooltipProps = {
  /**
   * The element that receives hover or focus to show the tooltip. Typically an icon button or any element
   * without a visible text label. Wrapped in a Radix-style trigger button — use `triggerRender` instead if
   * you want your element to be the trigger directly (e.g. to avoid nested buttons or layout wrappers).
   */
  trigger?: ReactNode;
  /**
   * Render the tooltip trigger AS a specific element instead of wrapping it. The element becomes the trigger
   * directly — no extra button or span. Use this when nesting buttons or when the wrapper changes the layout
   * (e.g. inside a CSS grid cell). Mutually exclusive with `trigger`.
   */
  triggerRender?: ReactElement;
  /** Tooltip content. Keep to one short phrase or sentence. */
  children?: React.ReactNode;
} & TooltipConfigs;

/**
 * Use `TenkoTooltip` to surface brief contextual help for elements that lack a visible text label — e.g.
 * icon buttons, truncated values, or fields with format constraints.
 *
 * Requires `TenkoTooltipProvider` in the ancestor tree (add it at layout level).
 *
 * @summary for brief contextual help on icon buttons and unlabeled elements
 */
export function TenkoTooltip({
  trigger,
  triggerRender,
  children,
  side,
  align,
  alignOffset,
  sideOffset,
  ...props
}: TenkoTooltipProps) {
  return (
    <Tooltip {...props}>
      {triggerRender ? <TooltipTrigger render={triggerRender} /> : <TooltipTrigger>{trigger}</TooltipTrigger>}
      <TooltipContent side={side} align={align} alignOffset={alignOffset} sideOffset={sideOffset}>
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

export { TooltipProvider as TenkoTooltipProvider };
